import hofCookie, { QS_PARAMS } from './cookie-data';
import hofEnv from './environment';
import logging from './logging';

/**
 * @param {URL} url
 * @param {boolean} replace
 */
const replaceUrl = (url, replace = true) => {
	if (replace) {
		window.location = url.toString();
	} else {
		window.location.href = url.toString();
	}
};

/**
 * Serializes URLSearchParams to a query string, omitting `=` for params with no value.
 * e.g. {params: '', c: 'daily'} → 'params&c=daily'
 * @param {URLSearchParams} params
 * @returns {string}
 */
const toSearchString = (params) => {
	const parts = [];
	params.forEach((value, key) => {
		parts.push(
			value === ''
				? encodeURIComponent(key)
				: `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
		);
	});
	return parts.join('&');
};

/**
 * @param {boolean} addCookie
 * @param {Record<string,string>|string|undefined} params
 */
export const buildCurrentQueryUrl = (addCookie = false, params = undefined) => {
	const qsParams = hofCookie.get(QS_PARAMS);

	const q = new URLSearchParams(
		addCookie && typeof qsParams !== 'undefined'
			? decodeURIComponent(qsParams)
			: undefined
	);

	if (params) {
		if (typeof params === 'object') {
			for (const [key, value] of Object.entries(params)) {
				q.set(key, value);
			}
		}

		if (typeof params === 'string') {
			const query = new URLSearchParams(decodeURIComponent(params));
			for (const [key, value] of query.entries()) {
				q.set(key, value);
			}
		}
	}

	return q;
};

/**
 * @param {string} pathname
 * @param {boolean} addCookie
 * @param {Record<string,any>|string|undefined} params
 *
 * @returns {URL}
 */
export const getUrl = (pathname, addCookie = false, params = undefined) => {
	try {
		const url = new URL(pathname, currentUrl.origin);

		url.search = toSearchString(buildCurrentQueryUrl(addCookie, params));

		return url;
	} catch (e) {
		throw new Error(e);
	}
};

/**
 * @param {URL|string} pathname
 * @param {boolean} replace
 * @param {boolean} addCookie
 * @param {Record<string,string>|undefined} params
 *
 * @returns {void}
 */
export const redirect = (
	pathname,
	replace = true,
	addCookie = false,
	params = undefined
) => {
	try {
		const newUrl =
			pathname instanceof URL ? pathname : getUrl(pathname, addCookie, params);

		if (newUrl.pathname === currentUrl.pathname && newUrl.searchParams.toString() === currentUrl.searchParams.toString()) {
			logging(
				'Redirect',
				() => {
					console.warn(
						'The URL is the same as the current URL',

						{ newUrl, pathname }
					);
				},
				'warning'
			);

			return;
		}

		replaceUrl(newUrl, replace);
	} catch (e) {
		console.error(e);
	}
};

/**
 * @deprecated
 * @param {string} externalUrl
 * @param {boolean} replace
 * @param {Record<string,string>|undefined} params
 *
 * @returns {void}
 */
export const redirectExternal = (
	externalUrl,
	replace = true,
	params = undefined
) => {
	try {
		const url = new URL(decodeURIComponent(externalUrl));
		if (params && typeof params === 'object') {
			for (const [key, value] of Object.entries(params)) {
				url.searchParams.set(key, value);
			}
		}
		replaceUrl(url, replace);
	} catch (e) {
		console.error(e);
	}
};

/**
 * @param {boolean|undefined} replace
 *
 * @returns {void}
 */
export const redirectHome = (replace = true) => redirect('/', replace);

/**
 * @type {URL}
 */
export const playNowUrl = getUrl('/play-now/', false);

export const isPlayNow = document.body.classList.contains(
	'page-template-iframe-page'
);

export const isHomePage = currentUrl.pathname === '/';

/**
 * @returns {void}
 */
export const redirectPlayNow = () => {
	logging('Redirect Play Now', () => {
		console.log(playNowUrl);
	});

	// @TODO start: remove this condition when whole issue which TSS page will be removed, VCTR-1746

	const isTSSPage = [
		'/tax-strategy-statements/',
		'/prime/',
		'/on-the-list/',
		'/hofcirclecontest-t-c/',
	].includes(currentUrl.pathname);

	console.log('isTSSPage', isTSSPage);

	if (isTSSPage) {
		logging(
			'Redirect TSS page',
			() => {
				console.warn('Skipping redirect to Play Now page');
			},
			'warning'
		);
		return;
	}

	// @TODO end

	redirect('/play-now/', true, true);
};

// Safari PWA: Auto-redirect after FB login
// In Safari PWA, the page reloads when returning from Facebook OAuth
if (hofEnv.isStandalonePWA) {
	const pendingRedirect = sessionStorage.getItem('hof_pwa_pending_redirect');

	if (pendingRedirect) {
		sessionStorage.removeItem('hof_pwa_pending_redirect');
		redirectPlayNow();
	}
}
